import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Select, Radio, Tooltip, Icon, Spin, message } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import { Chart, Geom, Axis, Tooltip as ChartTooltip, Legend } from 'bizcharts';
import { getAirbnbQualityStats } from 'utils/apis/integration';
import { SectionTitle } from './QualityDashboard.styles';

const { Option } = Select;

const RATE_METRICS = ['overall_issue_trip_rate', 'host_cancellation_rate'];
const COUNT_METRICS = ['cancellations_by_host', 'trips_cs_report_high_severity'];
const ALL_METRICS = RATE_METRICS.concat(COUNT_METRICS);

const LOOKBACK_OPTIONS = [
  { value: '1', labelKey: 'quality_dashboard.trends.lookback1m', fallback: '1 month' },
  { value: '3', labelKey: 'quality_dashboard.trends.lookback3m', fallback: '3 months' },
  { value: '6', labelKey: 'quality_dashboard.trends.lookback6m', fallback: '6 months' },
  { value: '12', labelKey: 'quality_dashboard.trends.lookback1y', fallback: '1 year' },
  { value: '24', labelKey: 'quality_dashboard.trends.lookback2y', fallback: '2 years' },
  { value: 'all', labelKey: 'quality_dashboard.trends.lookbackAll', fallback: 'All time' }
];

const SELECT_SEARCH_FILTER = (input, option) => {
  const label = option && option.props ? option.props.children : '';
  return String(label)
    .toLowerCase()
    .includes(String(input || '').toLowerCase());
};

function getMetricMeta() {
  return {
    overall_issue_trip_rate: {
      label: intl.get('quality_dashboard.qualityStats.metrics.issueTripRate').d('Issue trip rate'),
      shortLabel: intl.get('quality_dashboard.qualityStats.metrics.issueTripRate').d('Issue trip rate'),
      color: '#1890ff',
      kind: 'rate',
      tooltip: intl
        .get('quality_dashboard.qualityStats.metrics.issueTripRateTooltip')
        .d('Percentage of trips that had a quality-related issue during the month.')
    },
    host_cancellation_rate: {
      label: intl.get('quality_dashboard.qualityStats.metrics.cancellationRate').d('Cancellation rate'),
      shortLabel: intl.get('quality_dashboard.qualityStats.metrics.cancellationRate').d('Cancellation rate'),
      color: '#fa8c16',
      kind: 'rate',
      tooltip: intl
        .get('quality_dashboard.qualityStats.metrics.cancellationRateTooltip')
        .d('Percentage of trips you cancelled as the host during the month.')
    },
    cancellations_by_host: {
      label: intl.get('quality_dashboard.qualityStats.metrics.hostCancellations').d('Host cancellations'),
      shortLabel: intl.get('quality_dashboard.qualityStats.metrics.hostCancellations').d('Host cancellations'),
      color: '#722ed1',
      kind: 'count',
      tooltip: intl
        .get('quality_dashboard.qualityStats.metrics.hostCancellationsTooltip')
        .d('Number of trips you cancelled as the host during the month.')
    },
    trips_cs_report_high_severity: {
      label: intl.get('quality_dashboard.qualityStats.metrics.csReports').d('CS reports'),
      shortLabel: intl.get('quality_dashboard.qualityStats.metrics.csReports').d('CS reports'),
      color: '#eb2f96',
      kind: 'count',
      tooltip: intl
        .get('quality_dashboard.qualityStats.metrics.csReportsTooltip')
        .d('Number of trips with a serious guest support case during the month.')
    }
  };
}

function monthRowHasMetrics(row) {
  if (!row || !row.month) return false;
  return ALL_METRICS.some(k => {
    const v = row[k];
    return v != null && !Number.isNaN(Number(v));
  });
}

function monthlyHasAnyMetrics(monthly) {
  return (monthly || []).some(monthRowHasMetrics);
}

function QualityStatsEmptyChart({ height = 280 }) {
  return (
    <div
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa',
        borderRadius: 4,
        border: '1px solid #f0f0f0'
      }}
    >
      <div style={{ textAlign: 'center', padding: '0 24px' }}>
        <Icon type="line-chart" style={{ fontSize: 36, color: '#d9d9d9', marginBottom: 12 }} />
        <div style={{ fontSize: 14, color: '#8c8c8c' }}>{intl.get('quality_dashboard.qualityStats.emptyChart').d('No data for this period')}</div>
      </div>
    </div>
  );
}

function getMetricColor(metricKey) {
  const meta = getMetricMeta()[metricKey];
  return (meta && meta.color) || '#999';
}

function normalizeMonthlyStat(row) {
  if (!row) return { month: null };
  return {
    month: row.month,
    overall_issue_trip_rate: row.overallIssueTripRate != null ? row.overallIssueTripRate : row.overall_issue_trip_rate,
    host_cancellation_rate: row.hostCancellationRate != null ? row.hostCancellationRate : row.host_cancellation_rate,
    cancellations_by_host: row.cancellationsByHost != null ? row.cancellationsByHost : row.cancellations_by_host,
    trips_cs_report_high_severity: row.tripsCsReportHighSeverity != null ? row.tripsCsReportHighSeverity : row.trips_cs_report_high_severity
  };
}

function normalizeEntityType(type) {
  return type != null ? String(type).toLowerCase() : '';
}

function isUserStat(stat) {
  return normalizeEntityType(stat && stat.type) === 'user';
}

function isListingStat(stat) {
  return normalizeEntityType(stat && stat.type) === 'listing';
}

function formatListingDisplayLabel(name, listingId) {
  const id = listingId != null ? String(listingId) : '';
  const trimmed = name != null ? String(name).trim() : '';
  if (trimmed && id) return `${trimmed} (${id})`;
  if (trimmed) return trimmed;
  return id || '—';
}

function resolveListingDisplayLabel(stat, listingLabelById) {
  const entityId = stat && stat.listingId != null ? String(stat.listingId) : '';
  const apiName = stat && (stat.listingName || stat.listing_name);
  const fallbackName = listingLabelById && listingLabelById[entityId];
  return formatListingDisplayLabel(apiName || fallbackName, entityId);
}

function normalizeStatEntity(stat, listingLabelById) {
  const entityId = stat && stat.listingId != null ? String(stat.listingId) : '';
  const isAggregatedAllListings = !!(stat && (stat.isAggregatedAllListings === true || stat.is_aggregated_all_listings === true));
  const apiListingName = stat && (stat.listingName || stat.listing_name);
  const entityType = normalizeEntityType(stat && stat.type);
  let label;
  if (entityType === 'user' && isAggregatedAllListings) {
    label = (apiListingName && String(apiListingName).trim()) || intl.get('quality_dashboard.qualityStats.scopeAllListings').d('All Listings');
  } else if (isListingStat(stat)) {
    label = resolveListingDisplayLabel(stat, listingLabelById);
  } else {
    label = entityId;
  }
  return {
    entity_id: entityId,
    entity_type: entityType || stat.type,
    isAggregatedAllListings,
    label,
    monthly_quality_stats: ((stat && stat.monthlyQualityStats) || []).map(normalizeMonthlyStat)
  };
}

function isAggregatedAccountScope(entity) {
  return !!(entity && entity.entity_type === 'user' && entity.isAggregatedAllListings);
}

function getAccountScopeButtonLabel(userEntity) {
  if (!userEntity) return intl.get('quality_dashboard.qualityStats.scopeHost').d('Host');
  return isAggregatedAccountScope(userEntity)
    ? intl.get('quality_dashboard.qualityStats.scopeAllListings').d('All Listings')
    : intl.get('quality_dashboard.qualityStats.scopeHost').d('Host');
}

function getAccountScopeTooltip(userEntity) {
  if (!userEntity) {
    return intl
      .get('quality_dashboard.qualityStats.scopeHostUnavailable')
      .d(
        'Combined account totals are not available yet. You can still view each listing. Data usually appears after the next daily update from Airbnb.'
      );
  }

  return intl.get('quality_dashboard.qualityStats.scopeHostTooltip').d('Combined totals across all listings on this Airbnb account, from Airbnb.');
}

function getEntityBadge(activeEntity) {
  if (!activeEntity) return '—';
  if (activeEntity.entity_type === 'listing') {
    return intl
      .get('quality_dashboard.qualityStats.badgeListing', { label: activeEntity.label || activeEntity.entity_id })
      .d(`Listing · ${activeEntity.label || activeEntity.entity_id}`);
  }

  return intl.get('quality_dashboard.qualityStats.badgeHost').d('Host · Account totals from Airbnb');
}

function sortMonthlyStats(monthly) {
  return [...(monthly || [])].sort((a, b) => moment(a.month).valueOf() - moment(b.month).valueOf());
}

function formatMonthLabel(monthIso) {
  return moment(monthIso).format('MMM YYYY');
}

function formatRatePct(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function formatCount(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return String(Math.round(Number(value)));
}

function formatSignedDeltaPct(current, previous, decimals = 1) {
  if (current == null || previous == null) return '—';
  const delta = (Number(current) - Number(previous)) * 100;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(decimals)} pts`;
}

function formatSignedDeltaCount(current, previous) {
  if (current == null || previous == null) return '—';
  const delta = Number(current) - Number(previous);
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta}`;
}

function buildChartRows(monthly, metrics, valueTransform) {
  const metricMeta = getMetricMeta();
  const sorted = sortMonthlyStats(monthly);
  const rows = [];
  sorted.forEach(row => {
    const monthLabel = formatMonthLabel(row.month);
    metrics.forEach(metricKey => {
      const raw = row[metricKey];
      if (raw == null) return;
      rows.push({
        month: monthLabel,
        metric: metricMeta[metricKey].label,
        metricKey,
        value: valueTransform(raw, metricKey)
      });
    });
  });
  return rows;
}

function buildSummaryCards(monthly) {
  const metricMeta = getMetricMeta();
  const sorted = sortMonthlyStats(monthly);
  const complete = sorted.filter(row => ALL_METRICS.some(k => row[k] != null));
  if (complete.length === 0) return [];
  const latest = complete[complete.length - 1];
  const previous = complete.length > 1 ? complete[complete.length - 2] : null;

  return Object.keys(metricMeta).map(key => {
    const meta = metricMeta[key];
    const latestVal = latest[key];
    const prevVal = previous ? previous[key] : null;
    const isRate = meta.kind === 'rate';
    const wowDelta = latestVal != null && prevVal != null ? Number(latestVal) - Number(prevVal) : null;
    return {
      key,
      label: meta.shortLabel,
      color: meta.color,
      latestDisplay: isRate ? formatRatePct(latestVal) : formatCount(latestVal),
      wowDisplay: isRate ? formatSignedDeltaPct(latestVal, prevVal) : formatSignedDeltaCount(latestVal, prevVal),
      wowDelta,
      isRate
    };
  });
}

function buildQualityStatsApiQuery(accountId, filterQuery, extra) {
  const q = {
    ...(filterQuery || {}),
    airbnbId: accountId,
    airbnbUserId: accountId,
    ...extra
  };
  Object.keys(q).forEach(k => q[k] === undefined && delete q[k]);
  return q;
}

function fetchQualityStatsForAccount(accountId, filterQuery) {
  const baseQuery = buildQualityStatsApiQuery(accountId, filterQuery);
  return getAirbnbQualityStats(baseQuery).then(res => {
    const stats = (res && res.data && res.data.stats) || [];
    if (stats.some(isUserStat)) return stats;
    return getAirbnbQualityStats(buildQualityStatsApiQuery(accountId, filterQuery, { type: 'user' })).then(hostRes => {
      const hostStats = (hostRes && hostRes.data && hostRes.data.stats) || [];
      return hostStats.some(isUserStat) ? stats.concat(hostStats) : stats;
    });
  });
}

const QualityDashboardQualityStatsSection = ({ airbnbId, listingLabelById = {}, filterQuery = null }) => {
  const accountId = airbnbId;
  const [scope, setScope] = useState('user');
  const [listingId, setListingId] = useState(null);
  const [lookback, setLookback] = useState('6');
  const [loading, setLoading] = useState(false);
  const [userEntity, setUserEntity] = useState(null);
  const [listingEntities, setListingEntities] = useState([]);

  useEffect(() => {
    let cancelled = false;

    if (!accountId) {
      setUserEntity(null);
      setListingEntities([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    fetchQualityStatsForAccount(accountId, filterQuery)
      .then(stats => {
        if (cancelled) return;
        const rows = Array.isArray(stats) ? stats : [];
        const userRow = rows.find(isUserStat);
        const listingRows = rows.filter(isListingStat).map(s => normalizeStatEntity(s, listingLabelById));

        setUserEntity(userRow ? normalizeStatEntity(userRow, listingLabelById) : null);
        setListingEntities(listingRows);

        if (listingRows.length > 0) {
          setListingId(prev => {
            if (prev && listingRows.some(l => l.entity_id === prev)) return prev;
            return listingRows[0].entity_id;
          });
        } else {
          setListingId(null);
        }

        setScope(userRow ? 'user' : listingRows.length > 0 ? 'listing' : 'user');
      })
      .catch(err => {
        if (cancelled) return;
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          intl.get('quality_dashboard.errors.loadQualityStats').d('Failed to load quality statistics');
        message.error(msg);
        setUserEntity(null);
        setListingEntities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, listingLabelById, filterQuery]);

  const activeEntity = useMemo(() => {
    if (scope === 'user') return userEntity;
    if (!listingId) return listingEntities[0] || null;
    return listingEntities.find(l => l.entity_id === listingId) || listingEntities[0] || null;
  }, [scope, listingId, userEntity, listingEntities]);

  const filteredMonthly = useMemo(() => {
    if (!activeEntity || !activeEntity.monthly_quality_stats) return [];
    const sorted = sortMonthlyStats(activeEntity.monthly_quality_stats);
    if (lookback === 'all') return sorted;
    const months = Math.max(1, parseInt(lookback, 10) || 6);
    return sorted.slice(-months);
  }, [activeEntity, lookback]);

  const rateChartData = useMemo(
    () => buildChartRows(filteredMonthly, RATE_METRICS, (v, key) => (RATE_METRICS.includes(key) ? Number(v) * 100 : Number(v))),
    [filteredMonthly]
  );

  const countChartData = useMemo(() => buildChartRows(filteredMonthly, COUNT_METRICS, v => Number(v)), [filteredMonthly]);

  const summaryCards = useMemo(() => buildSummaryCards(filteredMonthly), [filteredMonthly]);

  const hasMetricsData = useMemo(() => monthlyHasAnyMetrics(filteredMonthly), [filteredMonthly]);

  const metricMeta = getMetricMeta();

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionTitle style={{ marginBottom: 4 }}>
        {intl.get('quality_dashboard.qualityStats.sectionTitle').d('Quality Statistics')}
        <Tooltip
          title={intl
            .get('quality_dashboard.qualityStats.sectionTooltip')
            .d('Monthly summary from Airbnb: trip issues, cancellations, and serious guest support cases.')}
        >
          <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 14 }} />
        </Tooltip>
      </SectionTitle>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 16 }}>{getEntityBadge(activeEntity)}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: '#595959' }}>{intl.get('quality_dashboard.qualityStats.scope').d('Scope:')}</span>
        <Radio.Group value={scope} onChange={e => setScope(e.target.value)} size="small" buttonStyle="solid" disabled={loading || !accountId}>
          <Tooltip title={getAccountScopeTooltip(userEntity)}>
            <span>
              <Radio.Button value="user" disabled={!userEntity}>
                {getAccountScopeButtonLabel(userEntity)}
              </Radio.Button>
            </span>
          </Tooltip>
          <Radio.Button value="listing" disabled={listingEntities.length === 0}>
            {intl.get('quality_dashboard.qualityStats.scopeListing').d('Listing')}
          </Radio.Button>
        </Radio.Group>
        {scope === 'listing' && listingEntities.length > 0 && (
          <>
            <span style={{ fontSize: 13, color: '#595959' }}>{intl.get('quality_dashboard.qualityStats.listingLabel').d('Listing:')}</span>
            <Select
              value={listingId || undefined}
              onChange={setListingId}
              style={{ minWidth: 320 }}
              size="small"
              disabled={loading}
              showSearch
              optionFilterProp="children"
              filterOption={SELECT_SEARCH_FILTER}
            >
              {listingEntities.map(l => (
                <Option key={l.entity_id} value={l.entity_id}>
                  {l.label}
                </Option>
              ))}
            </Select>
          </>
        )}
        <span style={{ fontSize: 13, color: '#595959', marginLeft: scope === 'listing' ? 0 : 8 }}>
          {intl.get('quality_dashboard.trends.lookback').d('Lookback:')}
        </span>
        <Select value={lookback} onChange={setLookback} style={{ width: 140 }} size="small" disabled={loading}>
          {LOOKBACK_OPTIONS.map(opt => (
            <Option key={opt.value} value={opt.value}>
              {intl.get(opt.labelKey).d(opt.fallback)}
            </Option>
          ))}
        </Select>
      </div>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card
              title={intl.get('quality_dashboard.qualityStats.ratesCardTitle').d('Quality rates (monthly)')}
              size="small"
              style={{ borderRadius: 8 }}
            >
              {hasMetricsData && rateChartData.length > 0 ? (
                <Chart height={280} data={rateChartData} forceFit padding={[24, 48, 48, 48]}>
                  <Axis name="month" />
                  <Axis name="value" label={{ formatter: v => `${v}%` }} />
                  <ChartTooltip />
                  <Legend visible={false} />
                  <Geom type="line" position="month*value" color={['metricKey', getMetricColor]} size={2} />
                  <Geom type="point" position="month*value" color={['metricKey', getMetricColor]} size={3} shape="circle" />
                </Chart>
              ) : (
                <QualityStatsEmptyChart />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title={intl.get('quality_dashboard.qualityStats.countsCardTitle').d('Quality counts (monthly)')}
              size="small"
              style={{ borderRadius: 8 }}
            >
              {hasMetricsData && countChartData.length > 0 ? (
                <Chart height={280} data={countChartData} forceFit padding={[24, 48, 48, 48]}>
                  <Axis name="month" />
                  <Axis name="value" />
                  <ChartTooltip />
                  <Legend visible={false} />
                  <Geom type="line" position="month*value" color={['metricKey', getMetricColor]} size={2} />
                  <Geom type="point" position="month*value" color={['metricKey', getMetricColor]} size={3} shape="circle" />
                </Chart>
              ) : (
                <QualityStatsEmptyChart />
              )}
            </Card>
          </Col>
        </Row>

        {hasMetricsData && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '16px 24px',
              marginBottom: 20,
              paddingTop: 4
            }}
          >
            {Object.keys(metricMeta).map(key => (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#262626' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: metricMeta[key].color, flexShrink: 0 }} />
                {metricMeta[key].label}
              </span>
            ))}
          </div>
        )}

        {summaryCards.length > 0 && (
          <Row gutter={[16, 16]}>
            {summaryCards.map(card => {
              const wowHas = card.wowDelta != null;
              const lowerIsBetter = card.wowDelta != null && card.wowDelta <= 0;
              const wowColor = !wowHas ? '#595959' : lowerIsBetter ? '#52c41a' : '#ff4d4f';
              return (
                <Col xs={24} sm={12} md={6} key={card.key}>
                  <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>{card.label}</div>
                      <Tooltip title={metricMeta[card.key].tooltip}>
                        <span style={{ fontSize: 12, color: card.color }}>●</span>
                      </Tooltip>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 18, fontWeight: 700, color: '#262626' }}>{card.latestDisplay}</div>
                    <div style={{ marginTop: 10, fontSize: 12, color: wowColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {wowHas ? (
                        <Icon type={lowerIsBetter ? 'arrow-down' : 'arrow-up'} style={{ color: wowColor }} />
                      ) : (
                        <span style={{ width: 14, display: 'inline-block' }} />
                      )}
                      {card.wowDisplay} {intl.get('quality_dashboard.charts.wow').d('WoW')}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: '#8c8c8c' }}>
                      {intl.get('quality_dashboard.qualityStats.latestMonth').d('Latest complete month')}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default QualityDashboardQualityStatsSection;
